/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { ComponentStore } from '@ngrx/component-store';
import { Observable, Subject } from 'rxjs';
import * as d3 from 'd3';
import { DevLoggerService } from 'dev-logger-service';

import {
    IAviBarGraphColors,
    IAviBarGraphDataPoint,
    TTimestampFormatFromApi,
    TTimestampFormatFromApiWithMicroS,
} from 'ng/modules/diagram/components/avi-bar-graph/avi-bar-graph.types';

import {
    ILogsGraphResponseData,
    ILogsGraphResponseResultData,
    TLogsGraphStateParams,
} from '../logs.types';

import { ILogsStates, LogsStore } from './logs.store';
import { LogsApiRequestService } from './logs-api-request.service';

export interface ILogsGraphStates {
    end: TTimestampFormatFromApiWithMicroS;
    start: TTimestampFormatFromApiWithMicroS;
    isLoading: boolean;
    hasError: boolean;
    legendList: IAviBarGraphColors,
    logsGraphResults: ILogsGraphResponseResultData[];
}

/**
 * Convert timestamp returned by api for individual data-points to standard time-format.
 */
function parseTime(input: TTimestampFormatFromApiWithMicroS): TTimestampFormatFromApi {
    const inputString = input as string;

    switch (inputString?.length) {
        case 32:
            return d3.timeParse('%Y-%m-%dT%H:%M:%S.%f%Z')(input as string);
        case 25:
            return d3.timeParse('%Y-%m-%dT%H:%M:%S%Z')(input as string);
    }

    return d3.timeParse('%Y-%m-%dT%H:%M:%S.%f%Z')(input as string);
}

/**
 * Only values which when altered, must prompt a new api request.
 */
export type TValuesAffectingGraph = Omit<ILogsGraphStates, 'isLoading'>;

/**
 * @abstract
 * @description
 *     An abstract store for state and effect management of log graph components.
 *     This store should never be used as a direct injectable.
 *     It should be inherited by more concrete log graph component stores.
 * @author Yogesh Patil
 */
export abstract class LogsGraphStore<T extends ILogsGraphStates> extends ComponentStore<T> {
    // ********************************** Selectors **********************************

    public readonly end$: Observable<TTimestampFormatFromApi> =
        this.select(({ end }) => parseTime(end));

    public readonly start$: Observable<TTimestampFormatFromApi> =
        this.select(({ start }) => parseTime(start));

    public readonly isLoading$ =
        this.select(({ isLoading }) => isLoading);

    public readonly hasError$ =
        this.select(({ hasError }) => hasError);

    // ****************************** Abstract Selectors ******************************

    /**
     * Selector returning params needed for log list components.
     */
    public abstract graphBarData$: Observable<IAviBarGraphDataPoint[]>;

    public readonly currentGraphColorsLegend$: Observable<IAviBarGraphColors>;

    // ******************************* Abstract Properties *****************************

    /**
     * Notify subscribers when the graph starts reloading data. Should be overridden by the concrete store.
     */
    public abstract readonly startLoadingSubject: Subject<void>;

    /**
     * The request ID for the log graph request. Should be overridden by the concrete store.
     */
    protected abstract logGraphRequestId: string;

    // ********************************** Constructor **********************************

    constructor(
        initialState: T,
        protected readonly logsStore: LogsStore<ILogsStates>,
        protected readonly logsApiRequestService: LogsApiRequestService,
        protected readonly devLoggerService: DevLoggerService,
    ) {
        super(initialState);
    }

    // ********************************** Methods **********************************

    /**
     * Clear error and timeout states and set loading states.
     */
    protected startLoading = (): void => {
        this.setErrorState(false);
        this.logsStore.setIsTimedOut(false);
        this.logsStore.setisErrorBannerEnabled(true);
        this.logsStore.setisTimeoutBannerEnabled(true);
        this.setIsLoading(true);
        this.logsStore.setIsGraphApiLoopInProgress(true);
        this.startLoadingSubject.next();
    };

    /**
     * Set isLoading to false.
     */
    protected stopLoading = (): void => {
        this.setIsLoading(false);
    };

    /**
     * Handle response from API once retrieved.
     */
    protected handleApiData = (data: ILogsGraphResponseData): void => {
        this.setGraphData(data);

        const { log_count: totalLogCount } = data;

        this.logsStore.setTotalLogCount(totalLogCount);
    };

    protected handleApiError = (err: any): void => {
        this.devLoggerService.error(err);
        this.setErrorState(true);
        this.stopLoading();
        this.logsStore.setIsGraphApiLoopInProgress(false);
    };

    /**
     * Set API loop state upon loop completion.
     */
    protected handleApiLoopComplete = (isTimedOut: boolean): void => {
        this.logsStore.setIsGraphApiLoopInProgress(false);

        if (isTimedOut) {
            this.logsStore.setIsTimedOut(true);
        }
    };

    /**
     * Set error state on local component store and module global store
     * (for component-level and page-level error messages).
     */
    protected setErrorState = (hasError: boolean): void => {
        this.logsStore.setHasError(hasError);
        this.patchState(state => ({
            ...state,
            hasError,
        }));
    };

    // ********************************** Updaters **********************************

    /**
     * Update the state with the graph data.
     */
    private readonly setGraphData = this.updater((state, data: ILogsGraphResponseData) => ({
        ...state,
        end: data.end,
        start: data.start,
        logsGraphResults: data.results,
    }));

    private readonly setIsLoading = this.updater((state, isLoading: boolean) => ({
        ...state,
        isLoading,
    }));

    // ******************************* Abstract Effects *******************************

    /**
     * Call logs API for logs graph data until all data is returned or timeout.
     * Upon each API return, it updates the state and makes a new request if needed.
     * Should be overridden by a concrete class.
     */
    public abstract getLogsGraphData: (apiParams: TLogsGraphStateParams) => void;

    /**
     * Since the getLogsGraphData call can be chained with exponentially increasing timeout,
     * This method should be called to cancel any ongoing log graph request; usually upon component destroy.
     * Should be overridden by a concrete class.
     */
    public abstract cancelLogsGraphRequest(): void;

    /**
     * Fetch updated logs graph data.
     * Need to be overridden by a concrete store.
     */
    public abstract refreshGraph(): void;
}
